import React from 'react';
import PrintLayout from '../PrintLayout';
import { ALL_ROLES_BY_ID, ROLES_BY_CATEGORY } from '../../../utils/roles';

const ALL_ROLE_DEFS = Object.values(ROLES_BY_CATEGORY).flat();

// Fixed 1280x720 page: footer starts at 650 (720 - 68px footer, minus safety margin).
// Rows are variable height (a feedback note adds a second sub-row), so pagination is
// budget-based rather than a fixed row count, and the roles-legend block (last page
// only) is reserved for before deciding which page is actually last.
const CONTENT_BOTTOM_PX = 650;
const HEADER_PX = 110; // top padding + title
const ROW_PX = 26;
const NOTE_ROW_PX = 38; // extra sub-row when a participant has a feedback note
const LEGEND_PX = 130;

function paginateRows(speakers, participantFeedback) {
  const budget = CONTENT_BOTTOM_PX - HEADER_PX;
  const rows = speakers.map(p => ({
    p,
    h: ROW_PX + ((participantFeedback[p.name]?.notes || '').trim() ? NOTE_ROW_PX : 0),
  }));

  const pages = [];
  let current = [];
  let currentH = 0;
  rows.forEach(r => {
    if (current.length && currentH + r.h > budget) {
      pages.push(current);
      current = [];
      currentH = 0;
    }
    current.push(r);
    currentH += r.h;
  });
  pages.push(current);

  // Reserve room for the legend on whichever page ends up last — bump trailing
  // rows to a new page if the legend wouldn't fit alongside them.
  const lastIdx = pages.length - 1;
  const rowsHeight = pages[lastIdx].reduce((s, r) => s + r.h, 0);
  if (pages[lastIdx].length && rowsHeight + LEGEND_PX > budget) {
    const overflow = [];
    while (
      pages[lastIdx].length &&
      pages[lastIdx].reduce((s, r) => s + r.h, 0) + LEGEND_PX > budget
    ) {
      overflow.unshift(pages[lastIdx].pop());
    }
    if (overflow.length) pages.push(overflow);
  }

  return pages.map(page => page.map(r => r.p));
}

const QUOTES = [
  'Everyone, especially you as the leader of the group, needs to be looking for places where people have different opinions but aren’t necessarily putting them out there. You need to be constantly mining for buried conflict.',
  'If the leader fails to demand that people weigh in, he or she is implicitly encouraging passive agreement.',
  'Bad meetings at the executive level usually indicate a huge gap between performance and potential.',
];

function formatDur(ms) {
  if (!ms) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function rolesPlayedFor(name, observedRoles) {
  return Object.entries(observedRoles || {})
    .filter(([, names]) => (names || []).includes(name))
    .map(([roleId]) => ALL_ROLES_BY_ID[roleId]?.name)
    .filter(Boolean);
}

export default function PrintTeamBreakdownV2({ pageNumber, reportData, meeting }) {
  const observedRoles = meeting.observedRoles || {};
  const allSpeakers = reportData.participation?.speakers || [];
  const activeSpeakers = allSpeakers.filter(s => s.ms > 0).sort((a, b) => b.ms - a.ms);
  const inquiryPerPerson = reportData.inquiry?.perPerson || [];
  const participantFeedback = meeting.participantFeedback || {};

  const TH = { padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'white', fontSize: 8.5, fontFamily: 'Montserrat, sans-serif' };
  const TD = { padding: '7px 12px', borderBottom: '1px solid rgba(42,61,91,0.12)', color: '#2a3d5b', fontSize: 9.5 };
  const TDC = { ...TD, textAlign: 'center' };

  const pages = paginateRows(activeSpeakers, participantFeedback);

  return pages.map((pageRows, pi) => (
    <PrintLayout key={pi} pageNumber={pi === 0 ? pageNumber : null}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex' }}>
        {pi === 0 && (
          <div style={{ width: '32%', background: '#1e2e45', padding: '6% 5%', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '2px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>
              Section 2: Healthy Behaviors
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: 16 }}>
              You can have the right content and the right process, but ultimately people have to behave in productive ways for a meeting to be effective. There are many different behaviors that come into play, as well as unwritten roles that people naturally assume.
            </div>
            {QUOTES.map((q, i) => (
              <div key={i} style={{ marginBottom: 12, padding: '8px 10px', borderLeft: '2px solid #5f7942', fontSize: 9.5, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
                "{q}"
              </div>
            ))}
            <div style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.3)' }}>
              — Patrick Lencioni, <i>Death by Meeting</i>
            </div>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, background: '#f5f4f0', padding: pi === 0 ? '5% 4%' : '5% 6%', overflow: 'hidden' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#1e2e45', marginBottom: 18 }}>
          Team Breakdown{pi > 0 ? ' (cont.)' : ''}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1e2e45' }}>
              <th style={{ ...TH, width: '20%' }}>Name</th>
              <th style={{ ...TH, textAlign: 'center' }}>Comments</th>
              <th style={{ ...TH, textAlign: 'center' }}>Questions</th>
              <th style={{ ...TH, textAlign: 'center' }}>Air Time</th>
              <th style={{ ...TH, width: '30%' }}>Roles Played</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((p, i) => {
              const inq = inquiryPerPerson.find(x => x.name === p.name) || { advocacy: 0, inquiry: 0 };
              const roles = rolesPlayedFor(p.name, observedRoles);
              const feedbackNote = (participantFeedback[p.name]?.notes || '').replace(/\s*\n+\s*/g, ' ').trim();
              const rowBg = i % 2 === 1 ? 'white' : undefined;
              return (
                <React.Fragment key={p.name}>
                  <tr style={{ background: rowBg }}>
                    <td style={{ ...TD, fontWeight: 700, borderBottom: feedbackNote ? 'none' : TD.borderBottom }}>{p.name.split('@')[0]}</td>
                    <td style={{ ...TDC, borderBottom: feedbackNote ? 'none' : TD.borderBottom }}>{inq.advocacy}</td>
                    <td style={{ ...TDC, borderBottom: feedbackNote ? 'none' : TD.borderBottom }}>{inq.inquiry}</td>
                    <td style={{ ...TDC, borderBottom: feedbackNote ? 'none' : TD.borderBottom }}>{formatDur(p.ms)} ({p.pct}%)</td>
                    <td style={{ ...TD, borderBottom: feedbackNote ? 'none' : TD.borderBottom }}>
                      {roles.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {roles.map(r => (
                            <span key={r} style={{
                              display: 'inline-block', padding: '1px 7px',
                              background: 'rgba(42,61,91,0.07)', border: '1px solid rgba(42,61,91,0.15)',
                              borderRadius: 8, fontSize: 8, color: '#2a3d5b',
                            }}>
                              {r}
                            </span>
                          ))}
                        </div>
                      ) : <span style={{ color: '#9aa2ab', fontStyle: 'italic', fontSize: 8.5 }}>None observed</span>}
                    </td>
                  </tr>
                  {feedbackNote && (
                    <tr style={{ background: rowBg }}>
                      <td style={TD} colSpan={5}>
                        <div style={{ fontSize: 8.5, fontStyle: 'italic', color: '#6b757e', lineHeight: 1.4 }}>
                          {feedbackNote}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td style={{ ...TD, fontStyle: 'italic', color: '#6b757e' }} colSpan={5}>No participation data</td>
              </tr>
            )}
          </tbody>
        </table>

        {pi === pages.length - 1 && (
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(42,61,91,0.12)' }}>
            <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6b757e', marginBottom: 6 }}>
              Roles reference
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 20, rowGap: 3 }}>
              {ALL_ROLE_DEFS.map(r => (
                <div key={r.id} style={{ fontSize: 8, color: '#4a5568', lineHeight: 1.4 }}>
                  <span style={{ fontWeight: 700, color: '#2a3d5b' }}>{r.name}</span> — {r.description}
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </PrintLayout>
  ));
}
